import React, { useMemo } from "react";
import {
  DialogPurchaseSessionPoints,
  MembershipProduct,
  MembershipProductCategories,
} from "../components/purchase-session-points-modal/purchase-session-points-modal.component";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  generateStripeCheckoutLink,
  getSessionEventProducts,
} from "../../mentor/services";
import useModal from "./useModal";

type SessionEventProduct = {
  id: number;
  name: string;
  category: string;
  price: number;
};

const useBuySessionPointsModal = () => {
  const { isShowing: showPurchaseDialog, toggle: togglePurchaseDialog } =
    useModal();

  const { data: sessionEventProducts } = useQuery({
    queryKey: ["session-event-products"],
    queryFn: getSessionEventProducts,
    refetchOnWindowFocus: false,
  });

  const sessionEventProductCategories =
    useMemo((): Array<MembershipProductCategories> => {
      let groupedProducts: Array<MembershipProductCategories> = [];

      if (!sessionEventProducts || sessionEventProducts.length === 0) {
        return groupedProducts;
      }

      groupedProducts = sessionEventProducts.reduce(
        (acc: Array<MembershipProductCategories>, e: SessionEventProduct) => {
          let category = acc.find((x) => x.billingCategory === e.category);

          if (!category) {
            category = {
              billingCategory: e.category,
              products: [],
            };
            acc.push(category);
          }

          category.products.push({
            id: e.id,
            name: e.name,
            price: e.price,
          });

          return acc;
        },
        []
      );

      return groupedProducts;
    }, [sessionEventProducts]);

  const { mutate: mutateStripeCheckout } = useMutation(
    generateStripeCheckoutLink,
    {
      onSuccess: (resultUrl) => (window.location.href = resultUrl),
    }
  );

  const handleAddToCart = async (product: MembershipProduct) => {
    togglePurchaseDialog();
    mutateStripeCheckout(product.id);
  };

  const buySessionPointsModal = (
    <DialogPurchaseSessionPoints
      products={sessionEventProductCategories}
      isOpen={showPurchaseDialog}
      onClose={() => togglePurchaseDialog()}
      onAddToCart={handleAddToCart}
    />
  );
  return { buySessionPointsModal, togglePurchaseDialog };
};

export default useBuySessionPointsModal;
